import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu, Dropdown, Avatar, Breadcrumb, Tag, theme } from 'antd';
import type { MenuProps } from 'antd';
import {
  HomeOutlined,
  DashboardOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  RiseOutlined,
  FileTextOutlined,
  CustomerServiceOutlined,
  SettingOutlined,
  LogoutOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useSession } from '../stores/session';
import { clearBrowserStorage } from '../utils/clear-browser-storage';
import { canSeeRoute } from './route-access';
import { roleLabel } from '../utils/role-labels';
import RouteErrorBoundary from './RouteErrorBoundary';

const { Sider, Header, Content } = Layout;

// 叶子项 key = 真实路由路径（点击导航）；分组 key = g-* （只展开/折叠，不导航）。
const MENU_ITEMS: MenuProps['items'] = [
  { key: '/', icon: <HomeOutlined />, label: '首页' },
  {
    key: 'g-advisor',
    icon: <TeamOutlined />,
    label: '顾问陪跑',
    children: [
      { key: '/advisor/customers', label: '客户' },
      { key: '/advisor/sales', label: '销售管理' },
      { key: '/advisor/sales-accounts', label: '销售账号' },
      { key: '/advisor/overlaps', label: '交叉用户' },
      { key: '/advisor/settings', label: '顾问设置' },
    ],
  },
  {
    key: 'g-dashboard',
    icon: <DashboardOutlined />,
    label: '数据看板',
    children: [
      { key: '/dashboard/north-star', label: '北极星看板' },
      { key: '/dashboard/print', label: '打印看板' },
      { key: '/dashboard/content', label: '内容看板' },
      { key: '/dashboard/creation-funnel', label: '创作漏斗(L0)' },
    ],
  },
  {
    key: 'g-trade',
    icon: <ShoppingCartOutlined />,
    label: '订单与财务',
    children: [
      { key: '/orders', label: '打印订单' },
      { key: '/refunds', label: '退款审批' },
      { key: '/packages', label: '付费档位' },
      { key: '/reconciliation', label: '财务对账' },
    ],
  },
  {
    key: 'g-user',
    icon: <UserOutlined />,
    label: '用户管理',
    children: [
      { key: '/users', label: '用户查询' },
      // 仅超级管理员可见（见下方 buildMenuItems 过滤）。
      { key: '/users/list-all', label: '全部用户' },
      { key: '/lifecycle', label: '删除回收' },
    ],
  },
  {
    key: 'g-growth',
    icon: <RiseOutlined />,
    label: '增长运营',
    children: [
      { key: '/boost', label: '助力运营' },
      { key: '/vouchers', label: '免单券' },
      { key: '/vouchers/requests', label: '发券申请' },
      { key: '/redeem-codes', label: '兑换码' },
      { key: '/partners', label: '城市合伙人' },
      { key: '/settlements', label: '佣金结算' },
      { key: '/shares', label: '分享召回' },
    ],
  },
  {
    key: 'g-content',
    icon: <FileTextOutlined />,
    label: '内容审核',
    children: [
      { key: '/ai-tasks', label: 'AI 任务' },
      { key: '/prompts', label: 'Prompt 管理' },
      { key: '/chapter-rollback', label: '章节回退' },
    ],
  },
  {
    key: 'g-support',
    icon: <CustomerServiceOutlined />,
    label: '客服',
    children: [{ key: '/tickets', label: '工单' }],
  },
  {
    key: 'g-system',
    icon: <SettingOutlined />,
    label: '系统',
    children: [
      { key: '/admins', label: '管理员' },
      { key: '/feature-flags', label: '功能开关' },
      { key: '/audit-logs', label: '审计日志' },
    ],
  },
];

/** 路由 → [分组名, 页面名]，供面包屑与浏览器标题使用。 */
const ROUTE_TRAIL: Record<string, [string, string]> = {};
for (const item of MENU_ITEMS ?? []) {
  if (item && 'children' in item && Array.isArray(item.children)) {
    for (const child of item.children) {
      if (child && 'key' in child && typeof child.key === 'string') {
        ROUTE_TRAIL[child.key] = [String(item.label), String((child as { label?: unknown }).label ?? '')];
      }
    }
  }
}

/** 详情页等子路由回退到最近的菜单父级（/orders/123 → /orders）。 */
function resolveTrail(pathname: string): { group: string | null; page: string; menuKey: string } {
  if (pathname === '/') return { group: null, page: '首页', menuKey: '/' };
  const parts = pathname.split('/').filter(Boolean);
  for (let n = parts.length; n >= 1; n--) {
    const key = '/' + parts.slice(0, n).join('/');
    const hit = ROUTE_TRAIL[key];
    if (hit) return { group: hit[0], page: hit[1], menuKey: key };
  }
  return { group: null, page: '', menuKey: pathname };
}

const ALL_GROUP_KEYS = ['g-advisor', 'g-dashboard', 'g-trade', 'g-user', 'g-growth', 'g-content', 'g-support', 'g-system'];

/** 按角色裁剪菜单：超管全量；其余按读权限过滤叶子项，清空的分组整组不显示。 */
function buildMenuItems(role: string | null): MenuProps['items'] {
  if (role === 'super_admin') return MENU_ITEMS;
  return (MENU_ITEMS ?? []).flatMap((item) => {
    if (item && 'children' in item && Array.isArray(item.children)) {
      const children = item.children.filter(
        (child) => !(child && 'key' in child && typeof child.key === 'string' && !canSeeRoute(role, child.key)),
      );
      return children.length ? [{ ...item, children }] : [];
    }
    return [item];
  });
}

/** 与 Sider breakpoint="lg"（<992px）一致；首帧就按视口决定，避免窄屏闪一下展开的侧栏。 */
function isNarrowViewport(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(max-width: 991.98px)').matches
    : false;
}

export default function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const role = useSession((s) => s.role);
  const actorId = useSession((s) => s.actorId);
  const clearSession = useSession((s) => s.clearSession);
  const [collapsed, setCollapsed] = useState(isNarrowViewport);
  // 窄屏（<lg）：侧栏收成 0 宽抽屉，展开时浮在内容上，不挤压页面
  const [narrow, setNarrow] = useState(isNarrowViewport);
  const { token } = theme.useToken();
  const menuItems = buildMenuItems(role);
  const contentRef = useRef<HTMLElement | null>(null);
  const trail = resolveTrail(location.pathname);

  // 切页：浏览器标题跟随当前页；内容区回到顶部（内容区是独立滚动容器，路由切换不会自动复位）
  useEffect(() => {
    document.title = trail.page ? `${trail.page} · 经年叙运营后台` : '经年叙 · 运营后台';
    contentRef.current?.scrollTo?.({ top: 0 });
  }, [location.pathname, trail.page]);

  function handleLogout() {
    // 先清 zustand 内存态（立即触发 UI 登出），再彻底清浏览器本地存储
    // （localStorage/sessionStorage/IndexedDB，批次1 E1，Design §9.2 共享机器场景）。
    clearSession();
    clearBrowserStorage();
    navigate('/login', { replace: true });
  }

  const userMenu: MenuProps['items'] = [
    { key: 'who', label: `ID: ${actorId ?? '-'}`, disabled: true },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', onClick: handleLogout },
  ];

  return (
    // height 而非 minHeight：让两栏各自滚动。用 minHeight 时整页共用浏览器
    // 滚动条，内容一长侧边栏就被一起顶走。
    <Layout style={{ height: '100vh' }}>
      <a className="admin-skip-link" href="#admin-main">
        跳到主内容
      </a>
      <Sider
        theme="light"
        breakpoint="lg"
        collapsedWidth={narrow ? 0 : 80}
        onBreakpoint={(broken) => {
          setNarrow(broken);
          setCollapsed(broken);
        }}
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        width={220}
        style={{
          borderInlineEnd: `1px solid ${token.colorBorderSecondary}`,
          height: '100vh',
          position: narrow && !collapsed ? 'fixed' : 'sticky',
          zIndex: 100,
          boxShadow: narrow && !collapsed ? '4px 0 16px rgba(60, 40, 20, .18)' : undefined,
          insetInlineStart: 0,
          top: 0,
          // 菜单项多于一屏时侧栏自己滚；sticky 保持占位，折叠时右侧自动跟随宽度
          // 窄屏收起时让「展开」小按钮（在侧栏 0 宽之外）可见，不被裁掉
          overflow: narrow && collapsed ? 'visible' : 'auto',
        }}
      >
        {/* 窄屏收起时整块隐藏（0 宽 + overflow 可见会让折叠菜单的浮层漏出来） */}
        <div style={{ display: narrow && collapsed ? 'none' : 'block' }}>
        <div
          style={{
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            fontWeight: 600,
            color: token.colorText,
            padding: '0 12px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          <span
            aria-hidden
            style={{
              width: 26,
              height: 26,
              flex: '0 0 auto',
              borderRadius: 8,
              background: `linear-gradient(135deg, ${token.colorPrimary}, #c08a52)`,
              color: '#fff',
              fontSize: 14,
              lineHeight: '26px',
              textAlign: 'center',
            }}
          >
            叙
          </span>
          {!collapsed && <span>经年叙 · 运营后台</span>}
        </div>
        <Menu
          mode="inline"
          selectedKeys={[trail.menuKey]}
          aria-label="主导航"
          defaultOpenKeys={ALL_GROUP_KEYS}
          items={menuItems}
          style={{ borderInlineEnd: 0 }}
          onClick={({ key }) => {
            // 仅叶子项（真实路由）导航；分组 key 以 'g-' 开头，只展开/折叠
            if (key.startsWith('/')) {
              navigate(key);
              if (narrow) setCollapsed(true);
            }
          }}
        />
        </div>
      </Sider>
      {/* 右栏独立成滚动容器：Header 钉住，只有 Content 滚 */}
      <Layout style={{ height: '100vh', overflow: 'hidden' }}>
        <Header
          className="admin-header"
          style={{
            flex: '0 0 auto',
            background: token.colorBgContainer,
            padding: '0 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <Breadcrumb
            items={[
              ...(trail.group ? [{ title: trail.group }] : []),
              { title: <span className="admin-breadcrumb-leaf">{trail.page || ' '}</span> },
            ]}
          />
          <Dropdown menu={{ items: userMenu }} placement="bottomRight" trigger={['click', 'hover']}>
            <button
              type="button"
              aria-label="账号菜单"
              style={{
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                border: 0,
                background: 'transparent',
                font: 'inherit',
                color: 'inherit',
                padding: '4px 8px',
                borderRadius: 8,
              }}
            >
              <Avatar size="small" style={{ background: token.colorPrimary }} icon={<UserOutlined />} />
              <Tag color="gold" style={{ margin: 0 }}>
                {roleLabel(role, '未登录')}
              </Tag>
            </button>
          </Dropdown>
        </Header>
        <Content
          id="admin-main"
          ref={contentRef}
          className="admin-content"
          style={{
            flex: 1,
            overflow: 'auto',
            margin: 24,
            padding: 24,
            background: token.colorBgContainer,
            borderRadius: token.borderRadiusLG,
            minHeight: 280,
          }}
        >
          <RouteErrorBoundary key={location.pathname}>
            <Outlet />
          </RouteErrorBoundary>
        </Content>
      </Layout>
    </Layout>
  );
}
